import { Navigate, Route, Routes } from 'react-router-dom';
import { SiteProvider } from './sites';
import { RequireAuth } from './auth';
import { Layout } from './components/Layout';
import { UpdateBand } from './appUpdate';
import { AgingPage } from './pages/AgingPage';
import { ConnectorPage } from './pages/ConnectorPage';
import { CustomersHome } from './pages/CustomersHome';
import { PastePage } from './pages/PastePage';
import { ForecastPage } from './pages/ForecastPage';
import { ExceptionsPage } from './pages/ExceptionsPage';
import { CustomerDetailPage } from './pages/CustomersPage';
import { NewCustomerPage } from './pages/CustomerForm';
import { ReceivePaymentPage } from './pages/ReceivePaymentPage';
import { VendorsPage } from './pages/VendorsPage';
import { HomePage } from './pages/HomePage';
import { InboxPage } from './pages/InboxPage';
import { VendorCodesPage } from './pages/VendorCodesPage';
import { LinkingSheetPage } from './pages/LinkingSheetPage';
import { CloverSettingsPage } from './pages/CloverSettingsPage';
import { PriceChangesPage } from './pages/PriceChangesPage';
import { ActivityPage } from './pages/ActivityPage';
import { CostsPricesPage } from './pages/CostsPricesPage';
import { IntelPage } from './pages/IntelPage';
import { PriceHistoryPage } from './pages/PriceHistoryPage';
import { InvoicePage } from './pages/InvoicePage';
import { ItemDetailPage, ItemsPage, NewItemPage } from './pages/ItemsPage';
import { LoginPage } from './pages/LoginPage';
import { LotsPage } from './pages/LotsPage';
import { TransfersPage } from './pages/TransfersPage';
import { SitesPage } from './pages/SitesPage';
import { UsersPage } from './pages/UsersPage';
import { PricesPage } from './pages/PricesPage';
import { PriceListPage } from './pages/PriceListPage';
import { InvoiceEditPage } from './pages/InvoiceEditPage';
import { PayablesPage } from './pages/PayablesPage';
import { SystemPage } from './pages/SystemPage';
import { LoadPage, OffersPage, OrderDayHome, OrderHistoryPage } from './pages/OrderDaySection';
import { VendorPricesPage } from './pages/VendorPricesPage';
import { InvoiceNewPage } from './pages/InvoiceNewPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { SalesPage } from './pages/SalesPage';
import { LostSalesPage } from './pages/LostSalesPage';
import { WorkloadPage } from './pages/WorkloadPage';
import { EmailSettingsPage } from './pages/EmailSettingsPage';
import { PaidPage, PaymentsSettingsPage } from './pages/PaymentsSettingsPage';
import { PublicDocumentPage } from './pages/PublicDocumentPage';
import { LedgerPage } from './pages/LedgerPage';
import { PacaLetterPage, PacaSettingsPage } from './pages/PacaPage';
import { PortalSettingsPage } from './pages/PortalSettingsPage';
import { LabelsPage, ScanPage, TraceExportPage, TracePage } from './pages/TracePage';
import { PasswordPage } from './pages/PasswordPage';
import { BlastPage, TextsPage } from './pages/TextsPage';
import { OAuthAuthorizePage } from './pages/OAuthAuthorizePage';
import { NewOrderPage } from './pages/NewOrderPage';
import { OrderDetailPage, OrdersPage } from './pages/OrdersPage';
import { PickPage } from './pages/PickPage';
import { PrepPage } from './pages/PrepPage';
import { PortalHomePage, PortalLayout, PortalOrderDetailPage, PortalInvoicePage, PortalOrderPage, PortalOrdersPage, PortalStatementPage } from './pages/PortalPages';
import { ProfitPage } from './pages/ProfitPage';
import { PnlPage } from './pages/PnlPage';
import { StalePricesPage } from './pages/StalePricesPage';
import { CompliancePage } from './pages/CompliancePage';
import { ReceivePage } from './pages/ReceivePage';
import { RoutePage } from './pages/RoutePage';
import { RoutePrintPage } from './pages/RoutePrintPage';
import { WorklistsPage } from './pages/WorklistsPage';
import { DepositSlipPage, EndOfDayPage } from './pages/EndOfDayPage';
import { MonthEndPage, StatementsPrintPage } from './pages/MonthEndPage';
import { StandingPage } from './pages/StandingPage';
import { StatementPage } from './pages/StatementPage';
import { NewVendorDocPage, VendorDocPage, VendorDocsPage } from './pages/VendorDocsPage';
import { PapersPage } from './pages/PapersPage';
import { StockPage } from './pages/StockPage';
import { LegalPage, QuickBooksPage } from './pages/QuickBooksPage';
import { QboMatchingPage } from './pages/QboMatchingPage';

export function App() {
  return (
    <>
    <UpdateBand />
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/legal/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/legal/terms" element={<LegalPage kind="terms" />} />
      {/* what a customer opens from an emailed invoice or statement: no sign-in (decision 93) */}
      <Route path="/d/:token" element={<PublicDocumentPage />} />
      {/* where Square sends the customer after paying (decision 94) */}
      <Route path="/paid" element={<PaidPage />} />
      <Route
        path="portal"
        element={
          <RequireAuth portal>
            <PortalLayout />
          </RequireAuth>
        }
      >
        <Route index element={<PortalHomePage />} />
        <Route path="order" element={<PortalOrderPage />} />
        <Route path="orders" element={<PortalOrdersPage />} />
        <Route path="orders/:id" element={<PortalOrderDetailPage />} />
        <Route path="account" element={<PortalStatementPage />} />
        <Route path="invoices/:id" element={<PortalInvoicePage />} />
      </Route>
      <Route
        element={
          <RequireAuth>
            <SiteProvider>
              <Layout />
            </SiteProvider>
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/new" element={<NewOrderPage />} />
        <Route path="orders/:id/edit" element={<NewOrderPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route path="items" element={<ItemsPage />} />
        <Route path="items/inbox" element={<InboxPage />} />
        <Route path="items/codes" element={<VendorCodesPage />} />
        <Route path="items/linking" element={<LinkingSheetPage />} />
        <Route path="items/new" element={<NewItemPage />} />
        <Route path="items/:id" element={<ItemDetailPage />} />
        <Route path="items/:id/intel" element={<IntelPage />} />
        <Route path="items/:id/history" element={<PriceHistoryPage />} />
        <Route path="stock" element={<StockPage />} />
        <Route path="stock/receive" element={<ReceivePage />} />
        <Route path="stock/pick" element={<PickPage />} />
        <Route path="stock/prep" element={<PrepPage />} />
        <Route path="stock/lots" element={<LotsPage />} />
        <Route path="stock/transfers" element={<TransfersPage />} />
        <Route path="stock/scan" element={<ScanPage />} />
        <Route path="stock/trace/:code" element={<TracePage />} />
        <Route path="stock/labels" element={<LabelsPage />} />
        <Route path="stock/trace-export" element={<TraceExportPage />} />
        <Route path="settings/sites" element={<SitesPage />} />
        <Route path="settings/users" element={<UsersPage />} />
        <Route path="prices" element={<PricesPage />} />
        <Route path="order-day" element={<OrderDayHome />} />
        <Route path="order-day/offers" element={<OffersPage />} />
        <Route path="order-day/history" element={<OrderHistoryPage key="history" />} />
        <Route path="order-day/shorts" element={<OrderHistoryPage key="shorts" shorts />} />
        <Route path="order-day/:load" element={<LoadPage />} />
        {/* the column desk is replaced by the invoice screen's cards (decision 60); old bookmarks land there */}
        <Route path="invoices/desk" element={<Navigate to="/invoices/new" replace />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/new" element={<InvoiceNewPage />} />
        <Route path="prices/list" element={<PriceListPage />} />
        <Route path="money/payables" element={<PayablesPage />} />
        <Route path="money/receive" element={<ReceivePaymentPage />} />
        <Route path="vendors" element={<VendorsPage />} />
        <Route path="settings/system" element={<SystemPage />} />
        <Route path="settings/activity" element={<ActivityPage />} />
        <Route path="settings/quickbooks" element={<QuickBooksPage />} />
        <Route path="settings/clover" element={<CloverSettingsPage />} />
        <Route path="qbo/matching" element={<QboMatchingPage />} />
        <Route path="settings/email" element={<EmailSettingsPage />} />
        <Route path="settings/payments" element={<PaymentsSettingsPage />} />
        <Route path="settings/paca" element={<PacaSettingsPage />} />
        <Route path="settings/portal" element={<PortalSettingsPage />} />
        <Route path="settings/ledger" element={<LedgerPage />} />
        <Route path="customers/:id/paca-letter" element={<PacaLetterPage />} />
        <Route path="settings/password" element={<PasswordPage />} />
        <Route path="reports/sales" element={<SalesPage />} />
        <Route path="lost-sales" element={<LostSalesPage />} />
        <Route path="reports/workload" element={<WorkloadPage />} />
        <Route path="reports/signals" element={<Navigate to="/reports/sales" replace />} />
        <Route path="reports/profit" element={<ProfitPage />} />
        <Route path="reports/pnl" element={<PnlPage />} />
        <Route path="reports/stale-prices" element={<StalePricesPage />} />
        <Route path="reports/price-changes" element={<PriceChangesPage />} />
        <Route path="reports/costs-prices" element={<CostsPricesPage />} />
        <Route path="compliance" element={<CompliancePage />} />
        <Route path="reports/exceptions" element={<ExceptionsPage />} />
        <Route path="reports/aging" element={<AgingPage />} />
        {/* Purchase planning was retired for the order day (decision 81): old links land there */}
        <Route path="buy" element={<Navigate to="/order-day" replace />} />
        <Route path="settings/connector" element={<ConnectorPage />} />
        <Route path="oauth/authorize" element={<OAuthAuthorizePage />} />
        <Route path="costs" element={<VendorDocsPage />} />
        <Route path="vendor-prices" element={<VendorPricesPage />} />
        <Route path="costs/bills" element={<VendorDocsPage kind="bills" key="bills" />} />
        <Route path="costs/price-lists" element={<VendorDocsPage kind="lists" key="lists" />} />
        <Route path="costs/papers" element={<PapersPage />} />
        <Route path="costs/new" element={<NewVendorDocPage />} />
        <Route path="costs/:id" element={<VendorDocPage />} />
        {/* the CRM is a tab of Customers now (decision 169) */}
        <Route path="crm" element={<Navigate to="/customers?tab=followups" replace />} />
        <Route path="paste" element={<PastePage />} />
        <Route path="forecast" element={<ForecastPage />} />
        <Route path="texts" element={<TextsPage />} />
        <Route path="route" element={<RoutePage />} />
        <Route path="route/:id/print" element={<RoutePrintPage />} />
        <Route path="work" element={<WorklistsPage />} />
        <Route path="money/day" element={<EndOfDayPage />} />
        <Route path="money/month-end" element={<MonthEndPage />} />
        <Route path="reports/statements" element={<StatementsPrintPage />} />
        <Route path="money/deposits/:id" element={<DepositSlipPage />} />
        <Route path="texts/blast" element={<BlastPage />} />
        <Route path="customers" element={<CustomersHome />} />
        <Route path="customers/new" element={<NewCustomerPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        <Route path="customers/:id/statement" element={<StatementPage />} />
        <Route path="customers/:id/standing" element={<StandingPage />} />
        <Route path="invoices/:id" element={<InvoicePage />} />
        <Route path="invoices/:id/edit" element={<InvoiceEditPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </>
  );
}
